import { useState, useRef } from 'react';

const WEB_SUBMIT_FORM_URL = 'https://readdy.ai/api/form/d8ojdlp8gb8kvsd0oarg';

const patientDocs = [
  { key: 'nurse_record', num: '01', title: '간호기록지', desc: '병원에서 작성·발급하는 간호기록지', source: '병원 발급', required: true },
  { key: 'admission_discharge', num: '02', title: '입·퇴원 확인서', desc: '입원일·퇴원일·병명이 기재된 확인서', source: '병원 발급', required: true },
  { key: 'care_diary', num: '03', title: '간병일지', desc: '코리아케어러스 공식 양식으로 매일 작성한 간병일지', source: '회사 양식', required: true },
  { key: 'transfer_receipt', num: '04', title: '이체확인증', desc: '환자→간병인 계좌이체 내역 (메모: 간병비 지급)', source: '직접 캡쳐', required: true },
];

const companyDocs = [
  { key: 'usage_cert', num: '05', title: '간병인매칭서비스 이용내역 확인서', desc: '코리아케어러스를 통한 간병서비스 이용 내역을 증명하는 서류입니다.' },
  { key: 'member_cert', num: '06', title: '회원가입 사실확인서', desc: '환자(또는 가족)의 당사 회원가입 사실을 증명하는 서류입니다.' },
  { key: 'biz_cert', num: '07', title: '코리아케어러스 사업자등록증', desc: '당사의 정식 사업자 등록을 증명하는 서류입니다.' },
];

export default function DocumentSubmit() {
  // Patient doc selection
  const [selectedDocs, setSelectedDocs] = useState<Record<string, boolean>>({});
  const selectedCount = Object.values(selectedDocs).filter(Boolean).length;

  // Company doc selection
  const [selectedCompanyDocs, setSelectedCompanyDocs] = useState<Record<string, boolean>>({});
  const selectedCompanyCount = Object.values(selectedCompanyDocs).filter(Boolean).length;

  // Submit modal
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitForm, setSubmitForm] = useState({ name: '', phone: '', patientName: '', patientBirth: '', insurance: '', notes: '' });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitDone, setSubmitDone] = useState(false);
  const submitFormRef = useRef<HTMLFormElement>(null);

  // Issue modal
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [issueForm, setIssueForm] = useState({ name: '', phone: '', patientName: '', patientBirth: '', insurance: '', careStart: '', careEnd: '', notes: '' });
  const [issueLoading, setIssueLoading] = useState(false);
  const [issueError, setIssueError] = useState<string | null>(null);
  const [issueDone, setIssueDone] = useState(false);
  const issueFormRef = useRef<HTMLFormElement>(null);

  // Detail tooltip
  const [tooltipDoc, setTooltipDoc] = useState<string | null>(null);

  const toggleDoc = (key: string) => {
    setSelectedDocs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleCompanyDoc = (key: string) => {
    setSelectedCompanyDocs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!submitForm.name.trim() || !submitForm.phone.trim() || !submitForm.patientName.trim() || !submitForm.patientBirth.trim()) {
      setSubmitError('필수 항목을 모두 입력해주세요.');
      return;
    }

    setSubmitLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('name', submitForm.name);
      params.append('phone', submitForm.phone);
      params.append('patient_name', submitForm.patientName);
      params.append('patient_birth', submitForm.patientBirth);
      params.append('insurance', submitForm.insurance);
      params.append('notes', submitForm.notes);
      params.append('selected_docs', JSON.stringify(Object.keys(selectedDocs).filter((k) => selectedDocs[k])));

      await fetch(WEB_SUBMIT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      setSubmitDone(true);
    } catch {
      setSubmitError('신청 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
    setSubmitLoading(false);
  };

  const inputClass = 'w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#2E7D6B] focus:ring-1 focus:ring-[#2E7D6B] transition-colors bg-white placeholder-gray-300';
  const labelClass = 'block text-xs font-bold text-gray-600 mb-1.5';

  return (
    <section className="w-full py-16 bg-white">
      <div className="max-w-5xl mx-auto px-6 space-y-14">

        {/* 서류 흐름도 */}
        <div>
          <div className="text-center mb-6">
            <h2 className="text-lg font-bold text-gray-900">서류 흐름 한눈에 보기</h2>
            <p className="text-xs text-gray-500 mt-1">
              환자가 4종 서류를 회사에 제출하면, 회사가 3종 서류를 발급해 드립니다
            </p>
          </div>

          {/* Flow diagram */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-5">
            <div className="w-full md:w-auto rounded-2xl border p-5 text-center" style={{ backgroundColor: '#fafffe', borderColor: 'rgba(46,125,107,0.15)' }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: 'rgba(46,125,107,0.1)' }}>
                <i className="ri-user-heart-line text-xl" style={{ color: '#2E7D6B' }} />
              </div>
              <p className="text-sm font-bold text-gray-900 mb-1">환자(보호자)</p>
              <p className="text-[11px] text-gray-500">4종 서류 준비</p>
            </div>
            <div className="flex flex-col items-center">
              <i className="ri-arrow-right-line text-2xl hidden md:block" style={{ color: '#2E7D6B' }} />
              <i className="ri-arrow-down-line text-2xl md:hidden" style={{ color: '#2E7D6B' }} />
              <span className="text-[10px] font-semibold text-gray-400 whitespace-nowrap">서류 제출</span>
            </div>
            <div className="w-full md:w-auto rounded-2xl border-2 p-5 text-center" style={{ backgroundColor: '#f0f9f6', borderColor: '#2E7D6B' }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: '#2E7D6B' }}>
                <i className="ri-building-2-line text-xl text-white" />
              </div>
              <p className="text-sm font-bold text-gray-900 mb-1">코리아케어러스</p>
              <p className="text-[11px] text-gray-500">3종 서류 발급</p>
            </div>
            <div className="flex flex-col items-center">
              <i className="ri-arrow-right-line text-2xl hidden md:block" style={{ color: '#2E7D6B' }} />
              <i className="ri-arrow-down-line text-2xl md:hidden" style={{ color: '#2E7D6B' }} />
              <span className="text-[10px] font-semibold text-gray-400 whitespace-nowrap">전체 서류</span>
            </div>
            <div className="w-full md:w-auto rounded-2xl border p-5 text-center" style={{ backgroundColor: '#fafffe', borderColor: 'rgba(46,125,107,0.15)' }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: 'rgba(46,125,107,0.1)' }}>
                <i className="ri-bank-line text-xl" style={{ color: '#2E7D6B' }} />
              </div>
              <p className="text-sm font-bold text-gray-900 mb-1">보험회사</p>
              <p className="text-[11px] text-gray-500">보험금 지급</p>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-dashed" style={{ borderColor: 'rgba(46,125,107,0.2)' }} />
          </div>
          <div className="relative flex justify-center">
            <span className="px-4 text-xs font-bold bg-white" style={{ color: '#2E7D6B' }}>
              환자가 준비할 서류 4종 (클릭하여 선택)
            </span>
          </div>
        </div>

        {/* Patient documents - CLICKABLE */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: 'rgba(46,125,107,0.1)' }}>
              <i className="ri-user-heart-line text-lg" style={{ color: '#2E7D6B' }} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">환자가 준비해서 회사에 제출할 서류</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                제출할 서류를 클릭하여 선택한 후, 하단 버튼으로 한번에 제출하세요
              </p>
            </div>
            <div className="ml-auto flex-shrink-0">
              <span className="text-xs font-bold px-3 py-1.5 rounded-full" style={{ backgroundColor: selectedCount > 0 ? '#2E7D6B' : 'rgba(46,125,107,0.1)', color: selectedCount > 0 ? '#fff' : '#2E7D6B' }}>
                {selectedCount > 0 ? `${selectedCount}종 선택됨` : '총 4종'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {patientDocs.map((doc) => {
              const isSelected = !!selectedDocs[doc.key];
              return (
                <button
                  key={doc.key}
                  type="button"
                  onClick={() => toggleDoc(doc.key)}
                  className={`flex gap-4 rounded-2xl p-5 border-2 text-left cursor-pointer transition-all duration-200 relative ${
                    isSelected
                      ? 'border-[#2E7D6B] shadow-[0_0_0_4px_rgba(46,125,107,0.08)]'
                      : 'border-[rgba(46,125,107,0.12)] hover:border-[rgba(46,125,107,0.25)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]'
                  }`}
                  style={{ backgroundColor: isSelected ? '#f0f9f6' : '#fafffe' }}
                >
                  {/* Selection indicator */}
                  <div className="flex flex-col items-center gap-2 flex-shrink-0">
                    <div
                      className={`w-11 h-11 flex items-center justify-center rounded-xl transition-all duration-200 ${
                        isSelected ? 'scale-110' : ''
                      }`}
                      style={{ backgroundColor: isSelected ? '#2E7D6B' : 'rgba(46,125,107,0.1)' }}
                    >
                      {isSelected ? (
                        <i className="ri-check-line text-lg text-white" />
                      ) : (
                        <span className="text-sm font-black" style={{ color: '#2E7D6B' }}>{doc.num}</span>
                      )}
                    </div>
                    {isSelected && (
                      <span className="text-[9px] font-bold" style={{ color: '#2E7D6B' }}>선택완료</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="text-sm font-bold text-gray-900">{doc.title}</p>
                      {doc.required && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'rgba(46,125,107,0.1)', color: '#2E7D6B' }}>필수</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed">{doc.desc}</p>
                    <span className="inline-block mt-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(0,0,0,0.03)', color: '#6b7280' }}>
                      {doc.source}
                    </span>
                  </div>
                  {/* Hover hint */}
                  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-checkbox-blank-circle-line text-gray-300" style={{ display: isSelected ? 'none' : 'block' }} />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Submit CTA - appears when docs are selected */}
          {selectedCount > 0 && (
            <div className="mt-6 flex flex-col items-center gap-3">
              <p className="text-sm font-bold text-gray-700">
                선택한 <span style={{ color: '#2E7D6B' }}>{selectedCount}종</span> 서류를 제출합니다
              </p>
              <button
                type="button"
                onClick={() => setShowSubmitModal(true)}
                className="px-8 py-3.5 rounded-2xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-all flex items-center gap-2"
                style={{ backgroundColor: '#2E7D6B' }}
              >
                <i className="ri-upload-cloud-2-line text-base" />
                선택한 서류 제출하기
              </button>
              <p className="text-[11px] text-gray-400">
                또는 카카오 채팅방 / 팩스(070-7862-8654)로 보내주세요
              </p>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-dashed" style={{ borderColor: 'rgba(46,125,107,0.2)' }} />
          </div>
          <div className="relative flex justify-center">
            <span className="px-4 text-xs font-bold bg-white" style={{ color: '#1a6b5a' }}>
              회사가 발급하는 서류 3종 (클릭하여 선택)
            </span>
          </div>
        </div>

        {/* Company documents - CLICKABLE */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: 'rgba(26,107,90,0.1)' }}>
              <i className="ri-building-2-line text-lg" style={{ color: '#1a6b5a' }} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">회사가 발급해 드리는 서류</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                필요한 서류를 선택하고 발급 요청 버튼을 눌러주세요
              </p>
            </div>
            <div className="ml-auto flex-shrink-0">
              <span className="text-xs font-bold px-3 py-1.5 rounded-full" style={{ backgroundColor: selectedCompanyCount > 0 ? '#1a6b5a' : 'rgba(26,107,90,0.1)', color: selectedCompanyCount > 0 ? '#fff' : '#1a6b5a' }}>
                {selectedCompanyCount > 0 ? `${selectedCompanyCount}종 선택됨` : '총 3종'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {companyDocs.map((doc) => {
              const isSelected = !!selectedCompanyDocs[doc.key];
              return (
                <button
                  key={doc.key}
                  type="button"
                  onClick={() => toggleCompanyDoc(doc.key)}
                  className={`flex gap-4 rounded-2xl p-5 border-2 text-left cursor-pointer transition-all duration-200 ${
                    isSelected
                      ? 'border-[#1a6b5a] shadow-[0_0_0_4px_rgba(26,107,90,0.08)]'
                      : 'border-[rgba(26,107,90,0.1)] hover:border-[rgba(26,107,90,0.2)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]'
                  }`}
                  style={{ backgroundColor: isSelected ? '#f4faf8' : '#fafffe' }}
                >
                  <div className="flex flex-col items-center gap-2 flex-shrink-0">
                    <div
                      className={`w-11 h-11 flex items-center justify-center rounded-xl transition-all duration-200 ${
                        isSelected ? 'scale-110' : ''
                      }`}
                      style={{ backgroundColor: isSelected ? '#1a6b5a' : 'rgba(26,107,90,0.08)' }}
                    >
                      {isSelected ? (
                        <i className="ri-check-line text-lg text-white" />
                      ) : (
                        <span className="text-sm font-black" style={{ color: '#1a6b5a' }}>{doc.num}</span>
                      )}
                    </div>
                    {isSelected && (
                      <span className="text-[9px] font-bold" style={{ color: '#1a6b5a' }}>선택완료</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 mb-1">{doc.title}</p>
                    <p className="text-xs text-gray-500 leading-relaxed">{doc.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Issue CTA */}
          {selectedCompanyCount > 0 && (
            <div className="mt-6 flex flex-col items-center gap-3">
              <p className="text-sm font-bold text-gray-700">
                선택한 <span style={{ color: '#1a6b5a' }}>{selectedCompanyCount}종</span> 서류 발급을 요청합니다
              </p>
              <button
                type="button"
                onClick={() => setShowIssueModal(true)}
                className="px-8 py-3.5 rounded-2xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-all flex items-center gap-2"
                style={{ backgroundColor: '#1a6b5a' }}
              >
                <i className="ri-file-copy-2-line text-base" />
                선택한 서류 발급 요청하기
              </button>
            </div>
          )}
        </div>

      </div>

      {/* ============ SUBMIT MODAL ============ */}
      {showSubmitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowSubmitModal(false)} />
          <div className="relative bg-white rounded-3xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(46,125,107,0.1)' }}>
                  <i className="ri-upload-cloud-2-line text-sm" style={{ color: '#2E7D6B' }} />
                </div>
                <h3 className="text-base font-extrabold text-gray-900">서류 제출하기</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowSubmitModal(false)}
                className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors"
              >
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>

            {/* Selected docs summary */}
            <div className="flex flex-wrap gap-1.5 mb-4">
              {patientDocs.filter((d) => selectedDocs[d.key]).map((d) => (
                <span key={d.key} className="text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: 'rgba(46,125,107,0.08)', color: '#2E7D6B' }}>
                  {d.num} {d.title}
                </span>
              ))}
            </div>

            <form ref={submitFormRef} onSubmit={handleSubmitForm} data-readdy-form="" className="space-y-4">
              <div>
                <label className={labelClass}>신청인 성명 <span className="text-red-400">*</span></label>
                <input type="text" value={submitForm.name} onChange={(e) => setSubmitForm((p) => ({ ...p, name: e.target.value }))} placeholder="홍길동" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>연락처 <span className="text-red-400">*</span></label>
                <input type="text" value={submitForm.phone} onChange={(e) => setSubmitForm((p) => ({ ...p, phone: e.target.value }))} placeholder="010-0000-0000" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>환자 성명 <span className="text-red-400">*</span></label>
                <input type="text" value={submitForm.patientName} onChange={(e) => setSubmitForm((p) => ({ ...p, patientName: e.target.value }))} placeholder="홍길순" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>환자 생년월일 (6자리) <span className="text-red-400">*</span></label>
                <input type="text" value={submitForm.patientBirth} onChange={(e) => setSubmitForm((p) => ({ ...p, patientBirth: e.target.value }))} placeholder="500101" maxLength={6} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>보험사</label>
                <select value={submitForm.insurance} onChange={(e) => setSubmitForm((p) => ({ ...p, insurance: e.target.value }))} className={inputClass}>
                  <option value="">선택하세요</option>
                  <option value="삼성생명">삼성생명</option>
                  <option value="한화생명">한화생명</option>
                  <option value="교보생명">교보생명</option>
                  <option value="삼성화재">삼성화재</option>
                  <option value="현대해상">현대해상</option>
                  <option value="DB손해보험">DB손해보험</option>
                  <option value="KB손해보험">KB손해보험</option>
                  <option value="메리츠화재">메리츠화재</option>
                  <option value="기타">기타</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>추가 요청사항</label>
                <textarea value={submitForm.notes} onChange={(e) => setSubmitForm((p) => ({ ...p, notes: e.target.value }))} placeholder="추가로 필요한 서류나 특이사항이 있으면 적어주세요" maxLength={500} rows={3} className={`${inputClass} resize-none`} />
              </div>

              {submitError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-xl">
                  <i className="ri-error-warning-line text-red-400 text-sm" />
                  <p className="text-sm text-red-600">{submitError}</p>
                </div>
              )}

              {submitDone ? (
                <div className="text-center py-6">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#E8F5E9' }}>
                    <i className="ri-check-line text-2xl" style={{ color: '#2E7D6B' }} />
                  </div>
                  <p className="text-base font-extrabold text-gray-900 mb-1">제출 완료!</p>
                  <p className="text-sm text-gray-500">담당자가 확인 후 빠른 시일 내에 연락드립니다.</p>
                  <button
                    type="button"
                    onClick={() => { setShowSubmitModal(false); setSubmitDone(false); }}
                    className="mt-4 px-6 py-2.5 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: '#2E7D6B' }}
                  >
                    닫기
                  </button>
                </div>
              ) : (
                <button
                  type="submit"
                  disabled={submitLoading}
                  className="w-full py-3.5 rounded-2xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#2E7D6B' }}
                >
                  {submitLoading ? (
                    <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin" />제출 중...</span>
                  ) : (
                    '제출하기'
                  )}
                </button>
              )}
            </form>
          </div>
        </div>
      )}

      {/* ============ ISSUE MODAL ============ */}
      {showIssueModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowIssueModal(false)} />
          <div className="relative bg-white rounded-3xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(26,107,90,0.1)' }}>
                  <i className="ri-file-copy-2-line text-sm" style={{ color: '#1a6b5a' }} />
                </div>
                <h3 className="text-base font-extrabold text-gray-900">서류 발급 요청하기</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowIssueModal(false)}
                className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors"
              >
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>

            <div className="flex flex-wrap gap-1.5 mb-4">
              {companyDocs.filter((d) => selectedCompanyDocs[d.key]).map((d) => (
                <span key={d.key} className="text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: 'rgba(26,107,90,0.08)', color: '#1a6b5a' }}>
                  {d.num} {d.title}
                </span>
              ))}
            </div>

            <form ref={issueFormRef} onSubmit={(e) => {
              e.preventDefault();
              setIssueError(null);
              if (!issueForm.name.trim() || !issueForm.phone.trim() || !issueForm.patientName.trim()) {
                setIssueError('필수 항목을 모두 입력해주세요.');
                return;
              }
              setIssueLoading(true);
              // Simulate async submit
              setTimeout(() => {
                setIssueLoading(false);
                setIssueDone(true);
              }, 1000);
            }} data-readdy-form="" className="space-y-4">
              <div>
                <label className={labelClass}>신청인 성명 <span className="text-red-400">*</span></label>
                <input type="text" value={issueForm.name} onChange={(e) => setIssueForm((p) => ({ ...p, name: e.target.value }))} placeholder="홍길동" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>연락처 <span className="text-red-400">*</span></label>
                <input type="text" value={issueForm.phone} onChange={(e) => setIssueForm((p) => ({ ...p, phone: e.target.value }))} placeholder="010-0000-0000" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>환자 성명 <span className="text-red-400">*</span></label>
                <input type="text" value={issueForm.patientName} onChange={(e) => setIssueForm((p) => ({ ...p, patientName: e.target.value }))} placeholder="홍길순" className={inputClass} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>간병 시작일</label>
                  <input type="date" value={issueForm.careStart} onChange={(e) => setIssueForm((p) => ({ ...p, careStart: e.target.value }))} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>간병 종료일</label>
                  <input type="date" value={issueForm.careEnd} onChange={(e) => setIssueForm((p) => ({ ...p, careEnd: e.target.value }))} className={inputClass} />
                </div>
              </div>

              {issueError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-xl">
                  <i className="ri-error-warning-line text-red-400 text-sm" />
                  <p className="text-sm text-red-600">{issueError}</p>
                </div>
              )}

              {issueDone ? (
                <div className="text-center py-6">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#E8F5E9' }}>
                    <i className="ri-check-line text-2xl" style={{ color: '#1a6b5a' }} />
                  </div>
                  <p className="text-base font-extrabold text-gray-900 mb-1">발급 요청 완료!</p>
                  <p className="text-sm text-gray-500">영업일 기준 2~3일 내에 서류가 발급됩니다.</p>
                  <button
                    type="button"
                    onClick={() => { setShowIssueModal(false); setIssueDone(false); }}
                    className="mt-4 px-6 py-2.5 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: '#1a6b5a' }}
                  >
                    닫기
                  </button>
                </div>
              ) : (
                <button
                  type="submit"
                  disabled={issueLoading}
                  className="w-full py-3.5 rounded-2xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#1a6b5a' }}
                >
                  {issueLoading ? (
                    <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin" />요청 중...</span>
                  ) : (
                    '발급 요청하기'
                  )}
                </button>
              )}
            </form>
          </div>
        </div>
      )}

    </section>
  );
}